import React, { useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Ticket {
    ID: number;
    BOWLER_ID: number;
    BOWLER_EMAIL: string;
    REASON: string;
    CATEGORY: string;
    SUBCATEGORY: string;
    QUESTIONS: string;
    RESOLVED: number;
    CREATED_AT: string;
    ADMIN_ID: number;
    NOMBRECOMPLETO: string;
    FICHANAL: number;
    ADMIN_USERNAME: string;
}

const PAGE_SIZE = 5;

const AdminTickets: React.FC = () => {
    const [tab, setTab] = useState<'open' | 'resolved'>('open');
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const [expanded, setExpanded] = useState<number | null>(null);
    const [error, setError] = useState('');

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const resolved = tab === 'resolved' ? 1 : 0;
            const res = await fetch(
                `${BACKEND_URL}/api/support/tickets?page=${page}&limit=${PAGE_SIZE}&resolved=${resolved}`
            );
            const data = await res.json();
            if (res.ok) {
                setTickets(data.tickets || []);
                setTotalPages(data.totalPages || 1);
                setTotal(data.total || 0);
            } else {
                setError(data.error || 'Failed to load tickets');
            }
        } catch {
            setError('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    }, [tab, page]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    // Reset to page 1 when switching tabs
    const handleTabChange = (t: 'open' | 'resolved') => {
        setTab(t);
        setPage(1);
        setExpanded(null);
    };

    const handleResolve = async (id: number) => {
        setActionLoading(id);
        try {
            await fetch(`${BACKEND_URL}/api/support/tickets/${id}/resolve`, { method: 'PUT' });
            await fetchTickets();
            setExpanded(null);
        } finally {
            setActionLoading(null);
        }
    };

    const handleReopen = async (id: number) => {
        setActionLoading(id);
        try {
            await fetch(`${BACKEND_URL}/api/support/tickets/${id}/reopen`, { method: 'PUT' });
            await fetchTickets();
            setExpanded(null);
        } finally {
            setActionLoading(null);
        }
    };

    const toggleExpand = (id: number) => {
        setExpanded(prev => (prev === id ? null : id));
    };

    const formatDate = (raw: string) => {
        if (!raw) return '-';
        try {
            return new Date(raw).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch {
            return raw;
        }
    };

    return (
        <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            {/* Section Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div>
                    <h2 className="text-base font-bold text-slate-800">Support Tickets</h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                        {total} {total === 1 ? 'ticket' : 'tickets'}
                    </p>
                </div>
                {/* Tabs */}
                <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                    <button
                        onClick={() => handleTabChange('open')}
                        className={`px-5 py-2 text-sm font-semibold transition-colors ${
                            tab === 'open'
                                ? 'bg-white text-red-500 border-b-2 border-b-red-500'
                                : 'bg-white text-slate-400 hover:text-slate-700'
                        }`}
                    >
                        Open
                    </button>
                    <button
                        onClick={() => handleTabChange('resolved')}
                        className={`px-5 py-2 text-sm font-semibold border-l border-slate-200 transition-colors ${
                            tab === 'resolved'
                                ? 'bg-white text-green-600 border-b-2 border-b-green-600'
                                : 'bg-white text-slate-400 hover:text-slate-700'
                        }`}
                    >
                        Resolved
                    </button>
                </div>
            </div>

            {/* Content area with slate background so cards pop */}
            <div className="bg-slate-100 p-4 space-y-3 min-h-[120px]">
                {loading ? (
                    <div className="py-14 flex justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-600" />
                    </div>
                ) : error ? (
                    <div className="py-10 text-center text-red-500 text-sm bg-red-50 rounded-xl">{error}</div>
                ) : tickets.length === 0 ? (
                    <div className="py-14 text-center text-slate-400 bg-white rounded-xl border border-slate-200">
                        <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                        <p className="font-medium">No {tab} tickets</p>
                    </div>
                ) : (
                    tickets.map(ticket => (
                        <div key={ticket.ID}
                            className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${
                                expanded === ticket.ID
                                    ? 'border-blue-300 shadow-blue-100'
                                    : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                            }`}
                        >
                            {/* Collapsed Row */}
                            <button
                                onClick={() => toggleExpand(ticket.ID)}
                                className="w-full text-left px-5 py-4 flex items-center gap-4"
                            >
                                {/* Status badge */}
                                <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                                    ticket.RESOLVED
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-amber-100 text-amber-700'
                                }`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${ticket.RESOLVED ? 'bg-green-500' : 'bg-amber-500'}`} />
                                    {ticket.RESOLVED ? 'Resolved' : 'Open'}
                                </span>

                                {/* Bowler info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                        <span className="font-bold text-gray-900 text-sm">
                                            {ticket.NOMBRECOMPLETO || `Bowler #${ticket.BOWLER_ID}`}
                                        </span>
                                        <span className="text-xs text-slate-400 font-mono">TAT {ticket.FICHANAL}</span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5 truncate">{ticket.BOWLER_EMAIL}</p>
                                    <p className="text-xs text-blue-600 font-semibold mt-1 truncate">{ticket.REASON}</p>
                                </div>

                                {/* Date */}
                                <span className="hidden lg:block text-xs text-slate-400 flex-shrink-0 text-right">
                                    {formatDate(ticket.CREATED_AT)}
                                </span>

                                {/* Chevron */}
                                <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                                    expanded === ticket.ID ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                                }`}>
                                    <svg
                                        className={`w-4 h-4 transition-transform ${expanded === ticket.ID ? 'rotate-180' : ''}`}
                                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </button>

                            {/* Expanded Detail */}
                            {expanded === ticket.ID && (
                                <div className="border-t border-slate-200 bg-slate-50 px-5 py-5 space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {/* Ticket Info */}
                                        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2.5">
                                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Ticket Info</h4>
                                            <div className="text-sm space-y-1.5">
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500">Ticket ID</span>
                                                    <span className="font-mono font-bold text-slate-800">#{ticket.ID}</span>
                                                </div>
                                                <div className="flex justify-between gap-2">
                                                    <span className="text-slate-500 flex-shrink-0">Created</span>
                                                    <span className="text-slate-700 text-right text-xs">{formatDate(ticket.CREATED_AT)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500">Assigned to</span>
                                                    <span className="text-slate-800 font-semibold">{ticket.ADMIN_USERNAME || `Admin #${ticket.ADMIN_ID}`}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Bowler Info */}
                                        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2.5">
                                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Bowler</h4>
                                            <div className="text-sm space-y-1.5">
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500">Name</span>
                                                    <span className="text-slate-800 font-semibold">{ticket.NOMBRECOMPLETO || '-'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500">TAT ID</span>
                                                    <span className="font-mono text-slate-800">{ticket.FICHANAL || '-'}</span>
                                                </div>
                                                <div className="flex justify-between gap-2">
                                                    <span className="text-slate-500 flex-shrink-0">Email</span>
                                                    <span className="text-slate-700 text-right text-xs truncate">{ticket.BOWLER_EMAIL || '-'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Issue Details */}
                                    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Issue Details</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                            <div className="bg-slate-50 rounded-lg px-3 py-2.5">
                                                <p className="text-xs text-slate-400 mb-0.5 font-medium uppercase tracking-wide">Reason</p>
                                                <p className="text-slate-800 font-semibold">{ticket.REASON}</p>
                                            </div>
                                            <div className="bg-slate-50 rounded-lg px-3 py-2.5">
                                                <p className="text-xs text-slate-400 mb-0.5 font-medium uppercase tracking-wide">Category</p>
                                                <p className="text-slate-800 font-semibold">{ticket.CATEGORY}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-400 mb-1.5 font-medium uppercase tracking-wide">Description</p>
                                            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                                                {ticket.QUESTIONS && ticket.QUESTIONS.trim()
                                                    ? ticket.QUESTIONS
                                                    : <span className="text-slate-400 italic">No description provided.</span>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex justify-end gap-3 pt-1">
                                        {ticket.RESOLVED === 0 ? (
                                            <button
                                                onClick={() => handleResolve(ticket.ID)}
                                                disabled={actionLoading === ticket.ID}
                                                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition shadow-sm"
                                            >
                                                {actionLoading === ticket.ID ? (
                                                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                                ) : (
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                                Mark Resolved
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleReopen(ticket.ID)}
                                                disabled={actionLoading === ticket.ID}
                                                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition shadow-sm"
                                            >
                                                {actionLoading === ticket.ID ? (
                                                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                                ) : (
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                )}
                                                Re-open Ticket
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="border-t border-slate-200 px-6 py-4 flex items-center justify-between bg-white">
                    <p className="text-sm text-slate-500">
                        Page <span className="font-semibold text-slate-700">{page}</span> of {totalPages}
                    </p>
                    <div className="flex gap-1.5">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium disabled:opacity-40 hover:bg-slate-50 transition"
                        >
                            &larr; Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                            <button
                                key={p}
                                onClick={() => setPage(p)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${p === page
                                    ? 'bg-slate-900 text-white'
                                    : 'border border-slate-200 bg-white hover:bg-slate-50'}`}
                            >
                                {p}
                            </button>
                        ))}
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium disabled:opacity-40 hover:bg-slate-50 transition"
                        >
                            Next &rarr;
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTickets;
